
import { useState, useCallback, useEffect } from "react";
import { FormField, Form } from "@/components/dashboard/shops/types";
import { v4 as uuidv4 } from "uuid";

export const useFormFields = (form: Form | null) => {
  const [formFields, setFormFields] = useState<FormField[]>(
    form?.fields || []
  );
  
  // Mettre à jour les champs lorsque le formulaire change
  useEffect(() => {
    if (form?.fields) {
      // Garantir que les champs de téléphone ont toujours le type 'phone'
      const updatedFields = form.fields.map(field => {
        if (field.name === 'customerPhone' || field.name === 'Téléphone' || field.name.toLowerCase().includes('phone') || field.name.toLowerCase().includes('téléphone')) {
          return { ...field, type: 'phone' as FormField['type'], required: true, enabled: true };
        }
        return field;
      });
      setFormFields(updatedFields);
    }
  }, [form]);
  
  // Ensure phone fields are always required and enabled
  useEffect(() => {
    setFormFields(prevFields => 
      prevFields.map(field => {
        if (field.type === 'phone' || field.name === 'customerPhone' || field.name === 'Téléphone' || 
            field.name.toLowerCase().includes('phone') || field.name.toLowerCase().includes('téléphone')) {
          return { ...field, type: 'phone' as FormField['type'], required: true, enabled: true };
        }
        return field;
      })
    );
  }, []);

  // Create default form template
  const createFormTemplate = useCallback(() => {
    const defaultFields: FormField[] = [
      { id: uuidv4(), name: "customerName", label: "Nom Complet", enabled: true, required: true, type: "text" },
      { id: uuidv4(), name: "customerPhone", label: "Numéro De Téléphone", enabled: true, required: true, type: "phone" },
      { id: uuidv4(), name: "customerRegion", label: "Wilaya", enabled: true, required: true, type: "select" },
      { id: uuidv4(), name: "customerMunicipality", label: "Commune", enabled: true, required: true, type: "select" },
      { id: uuidv4(), name: "customerAddress", label: "Adresse", enabled: true, required: false, type: "text" },
      { id: uuidv4(), name: "customerEmail", label: "Email", enabled: false, required: false, type: "email" },
    ];
    
    setFormFields(defaultFields);
    return defaultFields;
  }, []);
  
  // Utiliser useCallback pour optimiser les performances et éviter les rerenders inutiles
  const toggleFieldEnabled = useCallback((id: string) => {
    console.log("toggleFieldEnabled called for id:", id);
    
    setFormFields(prevFields => {
      // Trouver le field actuel pour le log
      const currentField = prevFields.find(f => f.id === id);
      console.log("Current field state:", currentField);
      
      // Vérifier si c'est un champ téléphone par nom ou par type
      const isPhoneField = currentField?.type === 'phone' || 
                          currentField?.name === 'customerPhone' || 
                          currentField?.name === 'Téléphone' ||
                          (currentField?.name && (
                            currentField.name.toLowerCase().includes('phone') || 
                            currentField.name.toLowerCase().includes('téléphone')
                          ));
      
      // Ne pas modifier les champs de type téléphone
      if (isPhoneField) {
        console.log("Preventing toggle of phone field");
        return prevFields;
      }
      
      // Créer un nouveau tableau avec le champ mis à jour
      const newFields = prevFields.map(field => {
        if (field.id === id) {
          const newField = { ...field, enabled: !field.enabled };
          console.log("Field updated:", field.id, "from", field.enabled, "to", newField.enabled);
          return newField;
        }
        return field;
      });
      
      // Log du nouveau tableau pour vérification
      console.log("Updated fields array:", JSON.stringify(newFields));
      
      // Retourner une nouvelle référence d'array pour forcer le re-render
      return [...newFields];
    });
  }, []);
  
  const toggleFieldRequired = useCallback((id: string) => {
    setFormFields(prevFields => {
      // Vérifier si c'est un champ téléphone par nom ou par type
      const currentField = prevFields.find(f => f.id === id);
      const isPhoneField = currentField?.type === 'phone' || 
                          currentField?.name === 'customerPhone' || 
                          currentField?.name === 'Téléphone' ||
                          (currentField?.name && (
                            currentField.name.toLowerCase().includes('phone') || 
                            currentField.name.toLowerCase().includes('téléphone')
                          ));
      
      // Ne pas modifier les champs de type téléphone
      if (isPhoneField) {
        return prevFields;
      }
      
      return prevFields.map(field => 
        field.id === id ? { ...field, required: !field.required } : field
      );
    });
  }, []);
  
  const deleteField = useCallback((id: string) => {
    setFormFields(prevFields => {
      // Vérifier si c'est un champ téléphone par nom ou par type
      const fieldToDelete = prevFields.find(f => f.id === id);
      const isPhoneField = fieldToDelete?.type === 'phone' || 
                           fieldToDelete?.name === 'customerPhone' || 
                           fieldToDelete?.name === 'Téléphone' ||
                           (fieldToDelete?.name && (
                             fieldToDelete.name.toLowerCase().includes('phone') || 
                             fieldToDelete.name.toLowerCase().includes('téléphone')
                           ));
      
      // Ne pas supprimer les champs de type téléphone
      if (isPhoneField) {
        return prevFields;
      }
      
      return prevFields.filter(field => field.id !== id);
    });
  }, []);
  
  const addNewField = useCallback(() => {
    const newField: FormField = {
      id: uuidv4(),
      name: "nouveau_champ",
      label: "Nouveau champ",
      enabled: true,
      required: false,
      type: "text"
    };
    setFormFields(prevFields => [...prevFields, newField]);
  }, []);
  
  const updateFieldName = useCallback((id: string, name: string) => {
    setFormFields(prevFields => {
      // Vérifier si le nouveau nom suggère un champ de téléphone
      const isPhoneName = name.toLowerCase().includes('phone') || 
                         name.toLowerCase().includes('téléphone');
                         
      return prevFields.map(field => {
        if (field.id === id) {
          // Si le nom indique un téléphone, changer le type aussi
          if (isPhoneName) {
            return { ...field, name, label: name, type: 'phone' as FormField['type'], required: true, enabled: true };
          }
          return { ...field, name, label: name };
        }
        return field;
      });
    });
  }, []);
  
  const updateFieldType = useCallback((id: string, type: FormField['type']) => {
    setFormFields(prevFields => {
      // Si on change un champ en type téléphone, le rendre requis et activé
      if (type === 'phone') {
        return prevFields.map(field =>
          field.id === id ? { ...field, type, required: true, enabled: true } : field
        );
      }
      
      // Si on change le type d'un champ qui a un nom suggérant "téléphone"
      const field = prevFields.find(f => f.id === id);
      if (field && (field.name.toLowerCase().includes('phone') || field.name.toLowerCase().includes('téléphone'))) {
        // Forcer à garder le type 'phone'
        return prevFields.map(f => 
          f.id === id ? { ...f, type: 'phone' as FormField['type'], required: true, enabled: true } : f
        );
      }
      
      return prevFields.map(field =>
        field.id === id ? { ...field, type } : field
      );
    });
  }, []);

  return {
    formFields,
    setFormFields,
    toggleFieldEnabled,
    toggleFieldRequired,
    deleteField,
    addNewField,
    updateFieldName,
    updateFieldType,
    createFormTemplate
  };
};
